/**
 * The reference reactive runtime: dependency-tracked signals for Node, CLIs and plain unit tests.
 *
 * Separate from the contract it implements because the two have different weights. This file owns a
 * module-level scheduler — a pending set, a batch depth, the currently-tracking consumer — and that
 * state is why importing it is a decision rather than a formality.
 */
import { MdyComputedWriteError, MdyDestroyedScopeError } from "./reactivity-errors.js";
let activeConsumer = null;
/**
 * The computed currently recomputing, if any.
 *
 * Separate from `activeConsumer`, which an effect also occupies: an effect writing a signal is
 * ordinary and intended, and only a computed's body is a place where a write cannot mean anything.
 */
let activeComputed = null;
// ─── Shared effect scheduler (batch()/flush()) ──────────────────────────
//
// Every VanillaEffect schedules itself into one shared pending set instead of
// queueing its own microtask. This is what makes batch() and flush() real:
// batch() suppresses the drain while its callback runs and then drains once,
// synchronously, before returning; flush() drains synchronously too. Neither
// changes the default (non-batch, non-flush) timing — the first effect to go
// stale still queues exactly one microtask, same as before this milestone.
const pendingEffects = new Set();
let drainScheduled = false;
let batchDepth = 0;
function scheduleEffect(effectNode) {
    pendingEffects.add(effectNode);
    if (batchDepth > 0 || drainScheduled)
        return;
    drainScheduled = true;
    queueMicrotask(() => {
        drainScheduled = false;
        if (batchDepth === 0)
            drainPendingEffects();
    });
}
/** Runs every pending effect, looping until none remain (settles chained re-triggers too). */
function drainPendingEffects() {
    while (pendingEffects.size > 0) {
        const snapshot = [...pendingEffects];
        pendingEffects.clear();
        for (const effectNode of snapshot) {
            try {
                effectNode.runIfPending();
            }
            catch (error) {
                // One effect's uncaught error (no onError given — VanillaEffect's
                // own try/catch already handled it if one was provided) must not
                // stop sibling effects in this same drain pass from running, nor
                // corrupt the scheduler. Reporting via console.error (not a
                // rethrow): re-throwing from inside a microtask callback would
                // itself become an *unhandled* exception, which crashes a Node
                // process with no global handler — worse than the bug this fixes.
                console.error("[modyra] Uncaught error in effect:", error);
            }
        }
    }
}
function track(producer) {
    if (activeConsumer) {
        producer.consumers.add(activeConsumer);
        activeConsumer.producers.add(producer);
    }
}
function dropDependencies(consumer) {
    for (const producer of consumer.producers) {
        producer.consumers.delete(consumer);
    }
    consumer.producers.clear();
}
class VanillaSignal {
    _value;
    consumers = new Set();
    _equal;
    constructor(_value, equal) {
        this._value = _value;
        this._equal = equal ?? Object.is;
    }
    read() {
        track(this);
        return this._value;
    }
    write(value) {
        // Refused while a computed is recomputing, whatever the value: allowing it only when the value
        // differs would make the rule depend on the data, and a rule about purity cannot.
        if (activeComputed !== null)
            throw new MdyComputedWriteError();
        if (this._equal(this._value, value))
            return;
        this._value = value;
        for (const consumer of [...this.consumers])
            consumer.markStale();
    }
}
class VanillaComputed {
    _fn;
    consumers = new Set();
    producers = new Set();
    _equal;
    _value;
    _dirty = true;
    /** The last evaluation threw: dependents saw the failure, though the computed is still dirty. */
    _failed = false;
    constructor(_fn, equal) {
        this._fn = _fn;
        this._equal = equal ?? Object.is;
    }
    markStale() {
        if (this._dirty && !this._failed)
            return;
        this._dirty = true;
        this._failed = false;
        for (const consumer of [...this.consumers])
            consumer.markStale();
    }
    read() {
        track(this);
        if (this._dirty) {
            dropDependencies(this);
            const prev = activeConsumer;
            const prevComputed = activeComputed;
            // eslint-disable-next-line @typescript-eslint/no-this-alias -- subscriber stack: save/restore around recompute
            activeConsumer = this;
            activeComputed = this;
            const previousValue = this._value;
            const hadValue = !this._isInitial;
            try {
                this._value = this._fn();
            }
            catch (error) {
                // A computed that failed stays dirty so the next read retries, but its dependents have
                // already read the failure: the next source change must reach them, which `markStale`
                // would otherwise refuse for a computed that is dirty.
                this._failed = true;
                throw error;
            }
            finally {
                activeConsumer = prev;
                activeComputed = prevComputed;
            }
            // Re-apply equality after recompute so an unchanged derived value does
            // not propagate staleness to its own consumers (mirrors what a
            // native-signal framework's `computed({ equal })` guarantees).
            if (hadValue && this._equal(previousValue, this._value)) {
                this._value = previousValue;
            }
            this._isInitial = false;
            this._dirty = false;
            this._failed = false;
        }
        return this._value;
    }
    _isInitial = true;
}
class VanillaEffect {
    _fn;
    _onError;
    producers = new Set();
    _destroyed = false;
    _cleanups = [];
    constructor(_fn, _onError) {
        this._fn = _fn;
        this._onError = _onError;
        this._run();
    }
    get destroyed() {
        return this._destroyed;
    }
    markStale() {
        if (this._destroyed)
            return;
        scheduleEffect(this);
    }
    /** Called only from the shared drain — set membership was the schedule marker. */
    runIfPending() {
        if (this._destroyed)
            return;
        this._run();
    }
    _run() {
        this._runCleanups();
        dropDependencies(this);
        const prev = activeConsumer;
        // An effect is where a write belongs, and one may run while a computed is being read — a flush
        // reached from inside a read, for instance. Its body is not that computed's body.
        const prevComputed = activeComputed;
        // eslint-disable-next-line @typescript-eslint/no-this-alias -- subscriber stack: save/restore around recompute
        activeConsumer = this;
        activeComputed = null;
        try {
            this._fn((cleanup) => this._cleanups.push(cleanup));
        }
        catch (error) {
            if (this._onError)
                this._onError(error);
            else
                throw error;
        }
        finally {
            activeConsumer = prev;
            activeComputed = prevComputed;
        }
    }
    _runCleanups() {
        const cleanups = this._cleanups;
        this._cleanups = [];
        for (const cleanup of cleanups)
            cleanup();
    }
    destroy() {
        if (this._destroyed)
            return;
        this._destroyed = true;
        pendingEffects.delete(this);
        this._runCleanups();
        dropDependencies(this);
    }
}
class VanillaScope {
    _parent;
    id;
    _destroyed = false;
    _cleanups = [];
    _children = new Set();
    constructor(debugName, _parent) {
        this._parent = _parent;
        this.id = Symbol(debugName ?? "scope");
        if (_parent) {
            if (_parent.destroyed)
                throw new MdyDestroyedScopeError(_parent.id);
            _parent._children.add(this);
        }
    }
    get destroyed() {
        return this._destroyed;
    }
    run(fn) {
        if (this._destroyed)
            throw new MdyDestroyedScopeError(this.id);
        return fn();
    }
    onCleanup(cleanup) {
        if (this._destroyed)
            throw new MdyDestroyedScopeError(this.id);
        this._cleanups.push(cleanup);
    }
    destroy() {
        if (this._destroyed)
            return;
        this._destroyed = true;
        this._parent?._children.delete(this);
        for (const child of [...this._children])
            child.destroy();
        this._children.clear();
        const cleanups = this._cleanups;
        this._cleanups = [];
        for (const cleanup of cleanups)
            cleanup();
    }
}
/**
 * Standalone reactive engine: dependency-tracked signals, lazy cached
 * computeds and microtask-batched effects. Enough to run the whole form
 * engine outside any framework — the "decisive test":
 *
 * ```ts
 * const form = createForm({ email: field("") });
 * form.f.email.set("foo@bar.com");
 * console.log(form.f.email.errors());
 * ```
 *
 * works in Node with no framework installed. Await a microtask
 * (`await Promise.resolve()`) after writes when asserting on
 * effect-driven state (async validators, drafts, history).
 */
export function vanillaReactivity() {
    return {
        id: Symbol("vanilla"),
        kind: "vanilla",
        // graphInspection is not implemented — reporting it true would be a
        // fictitious capability.
        capabilities: {
            effects: true,
            effectOwnership: true,
            signalEquality: true,
            // A recomputed value that's equal to the previous one is reused (so a
            // consumer's `Object.is` on the returned value still short-circuits),
            // but staleness already propagated synchronously at write time before
            // the equality check runs — this does NOT stop a downstream
            // computed/effect from re-running. Best-effort, not a
            // full glitch-free guarantee, so this stays false.
            computedEquality: false,
            batching: true,
            deterministicFlush: true,
            directObservation: true,
            // Enforced, not merely intended: writing a signal while a computed recomputes throws.
            pureComputeds: true,
            graphInspection: false,
            // Builds with no browser globals present and re-runs its computations after a write, which
            // is what a snapshot taken away from a browser depends on. Asserted by the run that puts a
            // form built here and a form built in a browser side by side.
            serverSnapshots: true,
        },
        signal(initial, options) {
            const node = new VanillaSignal(initial, options?.equal);
            const read = (() => node.read());
            read.set = (value) => node.write(value);
            read.update = (fn) => node.write(fn(untrackedRead(() => node.read())));
            read.asReadonly = () => () => node.read();
            return read;
        },
        computed(fn, options) {
            const node = new VanillaComputed(fn, options?.equal);
            return () => node.read();
        },
        effect(fn, options) {
            if (options?.scope?.destroyed) {
                throw new MdyDestroyedScopeError(options.scope.id);
            }
            const node = new VanillaEffect(fn, options?.onError);
            const ref = {
                destroy: () => node.destroy(),
                get destroyed() {
                    return node.destroyed;
                },
            };
            options?.scope?.onCleanup(() => ref.destroy());
            return ref;
        },
        untracked: untrackedRead,
        createScope(options) {
            return new VanillaScope(options?.debugName, options?.parent);
        },
        batch(fn) {
            batchDepth++;
            try {
                return fn();
            }
            finally {
                batchDepth--;
                if (batchDepth === 0)
                    drainPendingEffects();
            }
        },
        flush() {
            // Deterministic, not "wait one tick": drainPendingEffects() loops
            // until settled, so a chain of effects each triggering the next all
            // resolve within this one flush() rather than needing N awaits.
            return Promise.resolve().then(() => {
                drainPendingEffects();
            });
        },
        observe(selector, listener, options) {
            // `options.timing` is accepted but not differentiated: vanilla only
            // has one timing model (microtask-scheduled, same as effect()) —
            // best-effort rather than rejecting the option.
            const equal = options?.equal ?? Object.is;
            let hasPrevious = false;
            let previous;
            const node = new VanillaEffect(() => {
                const current = selector();
                if (!hasPrevious) {
                    hasPrevious = true;
                    previous = current;
                    return; // no "previous" to report yet — only fire on later changes
                }
                if (equal(previous, current))
                    return;
                const prev = previous;
                previous = current;
                listener(current, prev);
            });
            return {
                destroy: () => node.destroy(),
                get destroyed() {
                    return node.destroyed;
                },
            };
        },
    };
}
function untrackedRead(fn) {
    // `activeComputed` is deliberately left alone: untracked says "do not depend on what I read", not
    // "this is no longer a computed". A write inside one is refused either way.
    const prev = activeConsumer;
    activeConsumer = null;
    try {
        return fn();
    }
    finally {
        activeConsumer = prev;
    }
}
